import Container from "../../../common/Container";
import {Box, Button, Divider, Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {State} from "../../../redux/store";
import {Company} from "../../../types/company";
import {NewCommonForm} from "./components/Common/formik";
import {FormikProps} from "formik";
import React, {useEffect, useState} from "react";
import {formValidation} from "../../../utils/formValidation";
import {RubricFields} from "./components/Rubric";
import {NewPlanAddressForm, NewPlanItemDatesForm, NewPlanRubricForm} from "../../../types/newPlanItem";
import {PlanItem} from "../../../types/plan";
import {AddressFields} from "./components/Address";
import {DatesFields} from "./components/Dates";
import {planService} from "../../../api/services/plan.service";
import {Response} from "../../../types/api";
import {AxiosError} from "axios";
import {useSnack} from "../../../utils/useSnack";
import {SET_PLAN_ITEM} from "../../../redux/reducers/planItem";
import {Skeleton} from "@mui/lab";
import {CommonFields} from "./components/Common";
import {PlanItemState} from "../../../utils/enums";

export default function NewPlanItem({
                                        planItemEditing, setPlanItemEditing
                                    }: {
    planItemEditing?: boolean;
    setPlanItemEditing?: React.Dispatch<React.SetStateAction<boolean>>
}) {

    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const state = useSelector((state: State) => state);
    const {snack} = useSnack();
    const dispatch = useDispatch();


    const company: Company = state.company.company;
    const planItem: PlanItem = state.planItem.planItem;
    const pathArray = location.pathname.split('/');
    const planId = +pathArray[pathArray.length - (planItemEditing ? 3 : 2)];

    const onClose = () => navigate(`/account-plan/${planId}`)

    const commonFormikRef = React.useRef<FormikProps<NewCommonForm>>(null);
    const rubricFormikRef = React.useRef<FormikProps<NewPlanRubricForm>>(null);
    const addressFormikRef = React.useRef<FormikProps<NewPlanAddressForm>>(null);
    const datesFormikRef = React.useRef<FormikProps<NewPlanItemDatesForm>>(null);
    const [loading, setLoading] = useState(planItemEditing);

    const formikRefs = [
        commonFormikRef,
        rubricFormikRef,
        addressFormikRef,
        datesFormikRef,
    ]

    const validateAll = () => formikRefs.forEach(ref => formValidation(ref));
    const allIsValid = () => formikRefs
        .map(ref => ref.current?.isValid)
        .every(item => item);
    const planItemId = planItemEditing ? +pathArray[pathArray.length - 1] : -1

    const getPlanItem = async () => {
        try {
            setLoading(true);
            const response = await planService.getPlanItem(company.id, planId, planItemId);
            if (response && response.data && response.status === "success") {
                dispatch({type: SET_PLAN_ITEM, payload: response.data})
            }
        } catch (error) {
            snack(t("snack.error.default"), "error");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (planItemEditing) {
            getPlanItem();
        }
    }, [planItemEditing, planItemId, company.id, planId, t, navigate]);


    const editingInitialState = planItemEditing && planItem ? {
        id: planItem.id,
        common: {
            companyId: company.id,
            auctionType: {
                value: planItem.auctionType,
                label: t(`auction_type.${planItem.auctionType}`)
            }
        },
        rubric: {
            productType: {
                value: planItem.productType,
                label: t(`product_type.${planItem.productType}`)
            },
            item: {
                id: null,
                productCode: planItem.productCode,
                productName: planItem.productName,
                productBrief: planItem.productBrief,
                unit: planItem.unit,
                price: !!planItem.price
                    ? String(planItem.price)
                    : "",
                amount: !!planItem.amount
                    ? String(planItem.amount)
                    : "",
            }
        },
        address: {
            items: planItem.addresses
        },
        dates: {
            auctionDate: planItem.auctionDate,
            deliveryDate: planItem.deliveryDate,
        }

    } : undefined;

    const disabled = !!planItem?.state && planItem?.state !== PlanItemState.draft

    const handleSubmit = async () => {
        if (!company) {
            return
        }

        const allRefsDefined = (
            commonFormikRef.current &&
            rubricFormikRef.current &&
            addressFormikRef.current &&
            datesFormikRef.current &&
            true
        );

        validateAll();

        if (allRefsDefined && allIsValid()) {
            const common = commonFormikRef.current.values;
            const rubric = rubricFormikRef.current.values;
            const dates = datesFormikRef.current.values;
            const address = addressFormikRef.current.values;

            const data = {
                id: null,
                addresses: address.items,
                productCode: rubric.item.productCode,
                productBrief: rubric.item.productBrief,
                price: rubric?.item?.price?.toString()?.includes(" ") ? +(rubric?.item?.price?.replace(/\s/g, "")) : +rubric.item.price,
                amount: rubric?.item?.amount?.toString()?.includes(" ") ? +(rubric?.item?.amount?.replace(/\s/g, "")) : +rubric.item.amount,
                productType: rubric.productType.value,
                auctionType: common.auctionType.value,
                auctionDate: dates.auctionDate!,
                deliveryDate: dates.deliveryDate!,
                unit: rubric.item.unit,
            };

            try {
                let response: Response<PlanItem>;
                if (planItemEditing) {
                    response = await planService.editPlanItem(company.id, planId, planItemId, data)
                } else {
                    response = await planService.createPlanItem(company.id, planId, data)
                }

                if (response && response.data && response.status === "success") {
                    snack(
                        planItemEditing
                            ? t("snack.success.plan_item_edited")
                            : t("snack.success.plan_item_created"),
                        "success"
                    );
                    navigate(`/account-plan/${planId}/plan-item/${response.data.id}`);
                    if (setPlanItemEditing) setPlanItemEditing(true);
                } else {
                    snack(t("snack.error.default"), "error")
                }
            } catch (error) {
                const err = error as AxiosError;
                console.log(err.response);
                snack(t("snack.error.default"), "error");
            }
        }
    }

    if (editingInitialState && loading) {
        return (
            <Grid container spacing={2}>
                {Array.from(new Array(4)).map((_, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Skeleton variant="rectangular" height={60}/>
                        <Skeleton width="80%"/>
                    </Grid>
                ))}
            </Grid>
        );
    }


    return <Box>
        <Container>
            <Grid container rowSpacing={3}>
                <CommonFields formikRef={commonFormikRef}
                              editingInitialState={editingInitialState?.common}
                              company={company} disable={disabled}
                />

                <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>

                <RubricFields formikRef={rubricFormikRef} editingInitialState={editingInitialState?.rubric} disabled={disabled}/>

                <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>

                <DatesFields formikRef={datesFormikRef} editingInitialState={editingInitialState?.dates} disable={disabled}/>

                <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>

                <AddressFields formikRef={addressFormikRef} editingInitialState={editingInitialState?.address} planItemEditing={!!planItemEditing}
                companyId={company.id} planId={planId} planItemId={planItemId} disabled={disabled}/>
            </Grid>

            <Divider sx={{mt: 6, mb: 2, width: "100%"}}/>

            <Grid item container spacing={2} justifyContent="center" sx={{pt: 2}}>
                <Grid item xs="auto">
                    <Button fullWidth variant="contained" size="large"
                            onClick={onClose} color="secondary"
                    >
                        {disabled ? t("common.return_back") : t("plan_item.cancel")}
                    </Button>
                </Grid>
                <Grid item xs="auto">
                    <Button fullWidth variant="contained" color="primary"
                            onClick={handleSubmit} size="large" disabled={disabled}>
                        {planItemEditing
                            ? t("plan_item.save_changes")
                            : t("plan_item.create_contest")
                        }
                    </Button>
                </Grid>
            </Grid>
        </Container>
    </Box>
}